import { useEffect, useRef } from "react";

import { useRouter } from "next/router";
import { reportMetaSession } from "../../utils/APICalls/APICalls";
import envConfig from "../../utils/env.config";
import { readMetaClickIds } from "../../utils/help/socialMediaPixels/metaClickIds";
import {
  markMetaSessionSent,
  shouldSendMetaSession,
} from "../../utils/help/socialMediaPixels/metaSession";
import { useAppSelector } from "./useReduxHooks";

/**
 * Meta's pixel writes `_fbp` during `fbq('init')`, which has not happened on
 * first paint. Reporting immediately would send a null `fbp` for every
 * first-time visitor, so wait for it -- but not forever, since a restaurant with
 * no pixel never writes one and an ad blocker may stop it appearing at all.
 */
const FBP_POLL_INTERVAL_MS = 300;
const FBP_MAX_WAIT_MS = 3_000;

/**
 * Sends the customer's Meta identifiers to the backend once per session.
 *
 * Gated on having a token rather than on the restaurant having the Conversions
 * API: the story asks for IP and user data "in all cases", and they are useful
 * beyond Meta -- the stored user agent is what answers "how many of our customers
 * are on iPhone". Gating on the Conversions API would collect nothing for most of
 * the fleet.
 */
export const useMetaSessionContext = () => {
  const router = useRouter();

  /**
   * The token this last reported for, rather than a plain "have we sent" flag.
   *
   * A logged-out browser still has a token -- `/user/create-visitor` mints a real
   * customer row for it -- so the session first lands on the *visitor's* id, and
   * that row is orphaned when login swaps the token. Keying the guard on the
   * token value means a login naturally re-arms it and the real customer gets a
   * row of their own, without the auth code needing to know Meta exists.
   *
   * Nothing is re-sent to Meta by this: the endpoint only writes to our database.
   */
  const sentForTokenRef = useRef<string | undefined>(undefined);

  /**
   * Read from redux, not from the cookie. The visitor token arrives
   * asynchronously, *after* this effect first runs -- a cookie read is not
   * reactive, so the hook would bail once and never fire again on a first visit.
   */
  const token = useAppSelector((state) => state.userInfo.token);

  const franchise = router.query?.franchise;

  useEffect(() => {
    if (!router.isReady || !token || sentForTokenRef.current === token) {
      return;
    }

    // The endpoint is customer-scoped; without a token there is nobody to
    // attach this to.
    if (!shouldSendMetaSession()) {
      return;
    }

    let elapsed = 0;

    const send = () => {
      // Ref before the request, not after: a failure must not retry on every
      // route change for the rest of the day.
      sentForTokenRef.current = token;
      markMetaSessionSent();

      const franchiseSlug = Array.isArray(franchise) ? franchise[0] : franchise;
      const restaurantId = parseInt(envConfig.restaurantId!);

      reportMetaSession({
        restaurantId: Number.isNaN(restaurantId) ? undefined : restaurantId,
        franchiseSlug,
        ...readMetaClickIds(),
      }).catch(() => {
        // Telemetry: never surface to the customer.
      });
    };

    const timer = setInterval(() => {
      elapsed += FBP_POLL_INTERVAL_MS;

      if (readMetaClickIds().fbp || elapsed >= FBP_MAX_WAIT_MS) {
        clearInterval(timer);
        send();
      }
    }, FBP_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [router.isReady, token, franchise]);
};
